import React from "react";
import { Link } from "react-router-dom";
import { MdOutlineReportProblem } from "react-icons/md";
import Card from "../../../../components/card";
import { ProjectRisk, ProjectRiskReason } from "../../../../interfaces/Dashboard";

/**
 * Projetos em risco, com o motivo explícito.
 *
 * O motivo importa mais que o número: "87% do escopo consumido" e "3 tarefas atrasadas" pedem ações
 * diferentes. Por isso `reasons[]` vira chip legível em vez de um badge genérico de "risco".
 */

const REASON_LABEL: Record<ProjectRiskReason, string> = {
  burn_over_100: "Escopo estourado",
  burn_over_85: "Escopo acima de 85%",
  projected_past_deadline: "Projeção passa do prazo",
  overdue_tasks: "Tarefas atrasadas",
  estimated_end_passed: "Prazo vencido",
};

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString("pt-BR", { month: "short", year: "numeric" }) : "—";

const ProjectsAtRiskList: React.FC<{ projects: ProjectRisk[] }> = ({ projects }) => (
  <Card extra="p-5">
    <div className="mb-1 flex items-center gap-2">
      <MdOutlineReportProblem className="h-5 w-5 text-amber-500" aria-hidden="true" />
      <h3 className="text-base font-bold text-navy-700 dark:text-white">Projetos em risco</h3>
    </div>
    <p className="mb-4 text-xs text-gray-700 dark:text-gray-300">
      Escopo consumido além do contratado, prazo estourado ou tarefas atrasadas.
    </p>

    {projects.length === 0 ? (
      <div className="rounded-2xl border border-dashed border-gray-200 py-8 text-center dark:border-gray-700">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Nenhum projeto em risco no momento.
        </p>
      </div>
    ) : (
      <div className="flex flex-col gap-2">
        {projects.map((p) => (
          <Link
            key={p.projectId}
            to="/admin/project-tasks"
            className="flex flex-col gap-2 rounded-2xl border border-gray-100 p-3 transition-colors hover:bg-gray-50 dark:border-white/10 dark:hover:bg-navy-700 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <p className="truncate font-semibold text-navy-700 dark:text-white">
                {p.projectName}
                {p.clientName && (
                  <span className="ml-1.5 text-xs font-normal text-gray-500 dark:text-gray-400">
                    · {p.clientName}
                  </span>
                )}
              </p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {p.reasons.map((r) => (
                  <span
                    key={r}
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      p.severity === "danger"
                        ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                        : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                    }`}
                  >
                    {REASON_LABEL[r] ?? r}
                    {r === "overdue_tasks" && p.overdueTaskCount > 0 && ` (${p.overdueTaskCount})`}
                  </span>
                ))}
              </div>
            </div>

            <div className="shrink-0 text-right">
              {p.rawPercentConsumed != null && (
                <p
                  className={`font-display text-xl font-bold tabular-nums ${
                    p.rawPercentConsumed >= 100
                      ? "text-red-600 dark:text-red-400"
                      : "text-amber-600 dark:text-amber-400"
                  }`}
                >
                  {p.rawPercentConsumed.toLocaleString("pt-BR", { maximumFractionDigits: 0 })}%
                </p>
              )}
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                {p.estimatedEndDate ? `Prazo ${fmtDate(p.estimatedEndDate)}` : "Sem prazo definido"}
              </p>
            </div>
          </Link>
        ))}
      </div>
    )}
  </Card>
);

export default ProjectsAtRiskList;
